import UPInput from 'components/input';
import { useTranslation } from 'react-i18next';
import {
  GENERAL__DELETE,
  KIOSK__END_REGISTRATION_ADD_BREAK,
  KIOSK__END_REGISTRATION_BREAK_END_LABEL,
  KIOSK__END_REGISTRATION_BREAK_START_LABEL,
  KIOSK__END_REGISTRATION_BREAKS_LABEL,
  KIOSK__END_REGISTRATION_BREAKS_OVERLAP,
  KIOSK__END_REGISTRATION_MAX_BREAKS,
} from 'translations/constants';
import { TKioskBreak } from 'types/kiosk/TKioskClockOutData';
import { formatKioskTimeInput } from '../kiosk-time-input';
import {
  KioskAddBreakRow,
  KioskBreakActionRow,
  KioskBreakEditorContainer,
  KioskBreakHint,
  KioskBreakItem,
  KioskBreaksLabel,
  KioskFieldRow,
  KioskInlineLink,
  KioskWarningMessage,
} from '../styles';

interface KioskBreakEditorProps {
  breaks: TKioskBreak[];
  onChange: (breaks: TKioskBreak[]) => void;
  overlappingIndices: number[];
  maxBreaks: number;
  disabled?: boolean;
}

const KioskBreakEditor = ({ breaks, onChange, overlappingIndices, maxBreaks, disabled }: KioskBreakEditorProps) => {
  const [t] = useTranslation();
  const atCap = breaks.length >= maxBreaks;

  const updateBreak = (index: number, patch: Partial<TKioskBreak>) =>
    onChange(breaks.map((b, i) => (i === index ? { ...b, ...patch } : b)));

  const addBreak = () => {
    if (!atCap && !disabled) onChange([...breaks, { startTime: '', endTime: '' }]);
  };

  const removeBreak = (index: number) => onChange(breaks.filter((_, i) => i !== index));

  return (
    <KioskBreakEditorContainer>
      <KioskBreaksLabel>{t(KIOSK__END_REGISTRATION_BREAKS_LABEL)}</KioskBreaksLabel>

      {breaks.map((b, index) => (
        // Index key is safe: rows are only added/removed at the ends and re-rendered wholesale.
        // eslint-disable-next-line react/no-array-index-key
        <KioskBreakItem key={index}>
          <KioskBreakActionRow>
            <KioskInlineLink
              type="button"
              onClick={() => removeBreak(index)}
              disabled={disabled}
              id={`kiosk-break-${index}-remove`}
            >
              {t(GENERAL__DELETE)}
            </KioskInlineLink>
          </KioskBreakActionRow>
          <KioskFieldRow>
            <UPInput
              value={b.startTime}
              onChange={e => updateBreak(index, { startTime: formatKioskTimeInput(b.startTime, e.target.value) })}
              label={t(KIOSK__END_REGISTRATION_BREAK_START_LABEL)}
              placeholder="HH:mm"
              maxLength={5}
              disabled={disabled}
              id={`kiosk-break-${index}-start`}
              inputClassName="white-background"
            />
            <UPInput
              value={b.endTime}
              onChange={e => updateBreak(index, { endTime: formatKioskTimeInput(b.endTime, e.target.value) })}
              label={t(KIOSK__END_REGISTRATION_BREAK_END_LABEL)}
              placeholder="HH:mm"
              maxLength={5}
              disabled={disabled}
              id={`kiosk-break-${index}-end`}
              inputClassName="white-background"
            />
          </KioskFieldRow>
        </KioskBreakItem>
      ))}

      {overlappingIndices.length > 0 && (
        <KioskWarningMessage>{t(KIOSK__END_REGISTRATION_BREAKS_OVERLAP)}</KioskWarningMessage>
      )}

      {atCap ? (
        <KioskBreakHint>{t(KIOSK__END_REGISTRATION_MAX_BREAKS)}</KioskBreakHint>
      ) : (
        <KioskAddBreakRow>
          <KioskInlineLink type="button" onClick={addBreak} disabled={disabled} id="kiosk-break-add">
            {t(KIOSK__END_REGISTRATION_ADD_BREAK)}
          </KioskInlineLink>
        </KioskAddBreakRow>
      )}
    </KioskBreakEditorContainer>
  );
};

KioskBreakEditor.defaultProps = { disabled: false };

export default KioskBreakEditor;
